'use client';

import { CustomImage as Image } from '..';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { useCartStore, useShallow } from '..';
import { useTranslations } from 'next-intl';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import {
  CurrencySymbol,
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { ProductType } from 'components/shared/Cart/types';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getTaxString } from 'components/shared/Cart/utils';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { Currency } from 'utils/Currency';
import { DotSpinner } from 'components/shared';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMediaDimBackdrop,
  ShopCardPlanTileMedia,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';
import styles from './ProductCardPowerPlug.module.css';

type ProductCardPowerPlugProps = {
  className?: string;
  title: string;
  description: string;
  imgSrc: string;
  year1: { price: number; variantId: string };
  year2?: { price: number; variantId: string; save: number };
  tag?: string;
  originalPrice?: string;
  discountPercentage?: string;
};

const ProductCardPowerPlugInner = forwardRef<
  HTMLDivElement,
  ProductCardPowerPlugProps
>(function ProductCardPowerPlugInner(
  {
    className,
    title,
    description,
    imgSrc,
    year1,
    year2,
    tag,
    originalPrice,
    discountPercentage,
  },
  ref,
) {
  const CartClient = useCartStore(
    useShallow((s) => ({
      store: s.store,
      taxString: s.taxString,
      currency: s.currency,
      addToCart: s.addToCart,
    })),
  );
  const { store: regionStore } = useRegionLocale();
  const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
  const tCart = useTranslations('cart');
  const resolvedTaxString =
    CartClient.taxString || getTaxString(resolvedStore, tCart);
  const [isLoading, setIsLoading] = useState(false);
  const [showYearSelect, setShowYearSelect] = useState(false);
  const [selectedYear, setSelectedYear] = useState<'Year1' | 'Year2' | null>(
    null,
  );
  const innerRef = useRef<HTMLDivElement>(null);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const currency =
    CurrencySymbol[(CartClient.currency ?? Currency.usd) as Currency];

  const hoverOut = useCallback(() => {
    setTimeout(() => {
      setShowYearSelect(false);
      setSelectedYear(null);
    }, 200);
  }, []);
  const formatPriceWithComma = useFormatPrice();

  useClickOutside(innerRef, hoverOut, showYearSelect);

  const getSelectedVariant = () => {
    if (selectedYear === 'Year2' && year2) return year2;
    return year1;
  };

  const handleAddToCart = async () => {
    const variant = getSelectedVariant();
    if (!variant.variantId) return;
    try {
      setIsLoading(true);
      await CartClient.addToCart([
        {
          shopifyVariantId: variant.variantId,
          productType:
            title === 'UltrahumanX Membership'
              ? ProductType.UHX
              : ProductType.GENERIC,
        },
      ]);
    } finally {
      setIsLoading(false);
      if (showYearSelect) hoverOut();
    }
  };

  const handleButtonClick = () => {
    if (!year2) {
      handleAddToCart();
    } else {
      setShowYearSelect(true);
    }
  };

  return (
    <ShopProductCardRoot
      ref={setRefs}
      className={[styles.root, className].filter(Boolean).join(' ')}
    >
      <ShopCardPlanTileMedia
        onClick={() => {
          setShowYearSelect(false);
          setSelectedYear(null);
        }}
      >
        {imgSrc && (
          <Image
            src={imgSrc}
            alt={title}
            height={448}
            width={300}
            style={{
              objectFit: 'cover',
              width: '100%',
            }}
          />
        )}
      </ShopCardPlanTileMedia>
      <ShopCardBody>
        <ShopCardSpecSection>
          <h3 className="title">{title}</h3>
          {description && <p className="description">{description}</p>}
          <br />
        </ShopCardSpecSection>

        <ShopCardFooterRow>
          <ShopCardPriceCluster>
            {year2 && (
              <span style={{ flexBasis: '100%' }}>
                {tCart('sharedCart.upsell.startsFrom')}
              </span>
            )}
            {originalPrice && discountPercentage && (
              <span className="striked">
                {currency}
                {formatPriceWithComma(parseFloat(originalPrice))}
              </span>
            )}
            {currency}
            {formatPriceWithComma(
              originalPrice && discountPercentage
                ? year1.price * (1 - parseFloat(discountPercentage) / 100)
                : year1.price,
            )}
            <span>{resolvedTaxString}</span>
          </ShopCardPriceCluster>
          <ShopCardPrimaryCta
            className={isLoading ? 'quickAdd disable' : 'quickAdd'}
            onClick={handleButtonClick}
            data-buttontype="add to cart"
            data-product={title}
            disabled={isLoading}
            aria-busy={isLoading}
            data-variantid={year1.variantId}
          >
            {isLoading ? (
              <DotSpinner color="#FFFFFF" />
            ) : (
              tCart('sharedCart.upsell.addToCart')
            )}
          </ShopCardPrimaryCta>
        </ShopCardFooterRow>
      </ShopCardBody>
      {tag ? <ShopCardTag><ShopCardTagText tag={tag} /></ShopCardTag> : null}

      {year2 && (
        <ShopCardMediaDimBackdrop
          className={showYearSelect ? 'open' : ''}
          onClick={hoverOut}
        />
      )}
      {year2 && (
        <ShopCardBottomSheet
          className={`sizeOverlay ${showYearSelect ? 'open' : ''} ${styles.bottomSheet}`}
          aria-hidden={!showYearSelect}
        >
          <div className="yearSelect">
              <div className="yearOptions">
                <button
                  className={`yearBtn ${
                    selectedYear === 'Year1' ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedYear('Year1')}
                  aria-pressed={selectedYear === 'Year1'}
                >
                  <span className="yearLabel">
                    {tCart('sharedCart.upsell.oneYear')}
                  </span>
                  <span className="yearPrice">
                    {currency}
                    {formatPriceWithComma(year1.price)}
                  </span>
                </button>
                <button
                  className={`yearBtn ${
                    selectedYear === 'Year2' ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedYear('Year2')}
                  aria-pressed={selectedYear === 'Year2'}
                >
                  <span className="yearLabel">
                    {tCart('sharedCart.upsell.twoYears')}
                  </span>
                  <span className="yearPrice">
                    {currency}
                    {formatPriceWithComma(year2.price)}
                  </span>
                  <span className="saveBadge">
                    {tCart('sharedCart.upsell.savePercent', {
                      percent: year2.save,
                    })}
                  </span>
                </button>
              </div>
              <button
                className={`addToCart ${
                  !selectedYear || isLoading ? 'disabled' : ''
                }`}
                onClick={handleAddToCart}
                disabled={!selectedYear || isLoading}
              >
                {isLoading ? (
                  <DotSpinner color="#fff" />
                ) : selectedYear ? (
                  <>
                    {tCart('sharedCart.upsell.addToCartFor', {
                      price: `${getCurrencySymbolFromEnum((CartClient.currency ?? Currency.usd) as Currency)}${formatPriceWithComma(getSelectedVariant().price)}`,
                    })}
                  </>
                ) : (
                  tCart('sharedCart.upsell.selectPlan')
                )}
              </button>
            </div>
        </ShopCardBottomSheet>
      )}
    </ShopProductCardRoot>
  );
});

export const ProductCardPowerPlug = ProductCardPowerPlugInner;
